/**
 * CRUD client for user-saved report generation configurations
 * (backend/app/api/api_report_templates.py: GET/POST/PATCH/DELETE
 * /reports/templates). Every call is scoped server-side to the bearer
 * token's user - there is nothing else to pass here.
 */
import { API_ENDPOINTS } from '../../../config/api';

// Fired on window after a successful create/update/delete so any mounted
// rail can refetch without prop-drilling a refresh callback across the tree
// (the "Save configuration" button lives in ReportWindow, several component
// levels away from the Saved rail on ReportsPage).
export const SAVED_TEMPLATES_CHANGED_EVENT = 'banyan:saved-templates-changed';

function authToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
}

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken()}` };
}

async function parseOrThrow(response) {
  const contentType = response.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await response.json().catch(() => null) : null;
  if (!response.ok) {
    throw new Error((body && body.detail) || `Request failed: ${response.status} ${response.statusText}`);
  }
  return body;
}

function notifyChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SAVED_TEMPLATES_CHANGED_EVENT));
  }
}

/** List the current user's saved configurations, most recently updated first. */
export async function listSavedTemplates({ favorites = false } = {}) {
  const response = await fetch(API_ENDPOINTS.reportTemplates.list(favorites), { headers: authHeaders() });
  return parseOrThrow(response);
}

/** Save a new configuration: { name, reportType, params }. */
export async function createSavedTemplate({ name, reportType, params }) {
  const response = await fetch(API_ENDPOINTS.reportTemplates.create, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ name, report_type: reportType, params }),
  });
  const out = await parseOrThrow(response);
  notifyChanged();
  return out;
}

/** Partially update a configuration: any of { name, params, is_favorite, touch_last_used }. */
export async function updateSavedTemplate(id, patch) {
  const response = await fetch(API_ENDPOINTS.reportTemplates.update(id), {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
  const out = await parseOrThrow(response);
  notifyChanged();
  return out;
}

/** Hard-delete a configuration. */
export async function deleteSavedTemplate(id) {
  const response = await fetch(API_ENDPOINTS.reportTemplates.delete(id), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  const out = await parseOrThrow(response);
  notifyChanged();
  return out;
}
